import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Badge } from '@components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { getCoupons, createCoupons, getCouponStats, importCouponCodes } from '@generated/server/coupons';
import { Search, Loader2, Plus, QrCode, Ticket, CheckCircle, Upload } from 'lucide-react';

export const Route = createFileRoute('/_app/coupons')({ component: CouponsManagement });

type Coupon = {
  id: string;
  name: string;
  column_: string | null;
  column: string | null;
  column__: string | null;
  column_1: Date | null;
  column_2: Date | null;
  column_3: {
    linkedItems: Array<{ id: string; name: string }>;
  } | null;
};

function CouponsManagement() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialog, setCreateDialog] = useState(false);
  const [couponCount, setCouponCount] = useState('10');
  const [prefix, setPrefix] = useState('KS');
  const [creating, setCreating] = useState(false);
  const [stats, setStats] = useState<Array<{ column: string; count: number }>>([]);
  const [importDialog, setImportDialog] = useState(false);
  const [importCodes, setImportCodes] = useState('');
  const [importing, setImporting] = useState(false);

  const loadCoupons = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await getCoupons({
        data: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: search || undefined,
          cursor: isLoadMore ? cursor || undefined : undefined,
        },
      });

      if (isLoadMore) {
        setCoupons([...coupons, ...(result?.items || [])]);
      } else {
        setCoupons(result?.items || []);
      }
      setCursor(result?.cursor || null);
    } catch (err) {
      console.error('Failed to load coupons:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getCouponStats();
      setStats(result || []);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  useEffect(() => {
    loadCoupons();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleCreateCoupons = async () => {
    setCreating(true);

    try {
      await createCoupons({
        data: {
          count: parseInt(couponCount),
          prefix,
        },
      });

      setCreateDialog(false);
      setCouponCount('10');
      setPrefix('KS');
      loadCoupons();
      loadStats();
    } catch (err) {
      console.error('Failed to create coupons:', err);
      alert('אירעה שגיאה ביצירת הקופונים');
    } finally {
      setCreating(false);
    }
  };

  const handleImportCoupons = async () => {
    setImporting(true);

    try {
      const codes = importCodes
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      if (codes.length === 0) {
        alert('יש להזין קודי קופונים');
        setImporting(false);
        return;
      }

      // Import in batches of 20
      const batchSize = 20;
      let totalImported = 0;

      for (let i = 0; i < codes.length; i += batchSize) {
        const batch = codes.slice(i, i + batchSize);
        const result = await importCouponCodes({ data: { codes: batch } });
        totalImported += result.imported;
      }

      setImportDialog(false);
      setImportCodes('');
      loadCoupons();
      loadStats();
      alert(`יובאו ${totalImported} קופונים בהצלחה`);
    } catch (err) {
      console.error('Failed to import coupons:', err);
      alert('אירעה שגיאה בייבוא הקופונים');
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;

    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline'; color: string }> = {
      זמין: { variant: 'default', color: 'text-[hsl(var(--chart-1))]' },
      הוקצה: { variant: 'secondary', color: 'text-[hsl(var(--accent))]' },
      נוצל: { variant: 'outline', color: 'text-[hsl(var(--muted-foreground))]' },
    };

    const config = variants[status] || { variant: 'secondary' as const, color: '' };

    return (
      <Badge variant={config.variant} className="gap-1">
        {status === 'זמין' && <CheckCircle className="h-3 w-3" />}
        {status === 'הוקצה' && <Ticket className="h-3 w-3" />}
        {status === 'נוצל' && <QrCode className="h-3 w-3" />}
        {status}
      </Badge>
    );
  };

  const getStatCount = (status: string) => {
    const stat = stats.find((s) => s.column === status);
    return stat?.count || 0;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[hsl(var(--foreground))]">ניהול קופונים</h1>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
              יצירה וניהול קופונים לכרטיסים מסובסדים
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setImportDialog(true)} variant="outline" className="rounded-full gap-2">
              <Upload className="h-4 w-4" />
              ייבוא קופונים
            </Button>
            <Button onClick={() => setCreateDialog(true)} className="rounded-full gap-2">
              <Plus className="h-4 w-4" />
              יצירת קופונים
            </Button>
          </div>
        </div>

        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[hsl(var(--chart-1))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">זמין</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('זמין')}
            </p>
          </div>

          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-[hsl(var(--accent))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">הוקצה</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('הוקצה')}
            </p>
          </div>

          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">נוצל</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('נוצל')}
            </p>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              placeholder="חיפוש לפי קוד קופון..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadCoupons();
              }}
              className="pr-10 rounded-full"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 rounded-full">
              <SelectValue placeholder="כל הסטטוסים" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">כל הסטטוסים</SelectItem>
              <SelectItem value="זמין">זמין</SelectItem>
              <SelectItem value="הוקצה">הוקצה</SelectItem>
              <SelectItem value="נוצל">נוצל</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={() => loadCoupons()} variant="outline" className="rounded-full">
            חיפוש
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-[12px] overflow-hidden border border-[hsl(var(--border))]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                    <tr>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-[hsl(var(--card-foreground))]">
                        קוד קופון
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-[hsl(var(--card-foreground))]">
                        סטטוס
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-[hsl(var(--card-foreground))]">
                        תעודת זהות משוייכת
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-[hsl(var(--card-foreground))]">
                        תאריך הקצאה
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-[hsl(var(--card-foreground))]">
                        רישום קשור
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[hsl(var(--popover))]">
                    {coupons.map((coupon) => (
                      <tr
                        key={coupon.id}
                        className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted))] transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-mono text-[hsl(var(--foreground))]">
                          {coupon.column_}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(coupon.column)}</td>
                        <td className="px-4 py-3 text-sm text-[hsl(var(--foreground))]">
                          {coupon.column__ || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-[hsl(var(--muted-foreground))]">
                          {coupon.column_1?.toLocaleDateString('he-IL') || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-[hsl(var(--foreground))]">
                          {coupon.column_3?.linkedItems?.[0]?.name || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {coupons.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-[hsl(var(--muted-foreground))]">לא נמצאו קופונים</p>
              </div>
            )}

            {cursor && (
              <div className="flex justify-center pt-4">
                <Button onClick={() => loadCoupons(true)} disabled={loadingMore} className="rounded-full">
                  {loadingMore ? (
                    <>
                      <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                      טוען...
                    </>
                  ) : (
                    'טען עוד'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>יצירת קופונים חדשים</DialogTitle>
            <DialogDescription>הזן את מספר הקופונים שברצונך ליצור ואת הקידומת</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="count">מספר קופונים</Label>
              <Input
                id="count"
                type="number"
                min={1}
                max={1000}
                value={couponCount}
                onChange={(e) => setCouponCount(e.target.value)}
                className="rounded-[10px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prefix">קידומת (אופציונלי)</Label>
              <Input
                id="prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                placeholder="KS"
                maxLength={5}
                className="rounded-[10px]"
              />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                דוגמה: {prefix}-{Date.now()}-ABC123
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)} disabled={creating}>
              ביטול
            </Button>
            <Button onClick={handleCreateCoupons} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  יוצר...
                </>
              ) : (
                `יצירת ${couponCount} קופונים`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ייבוא קודי קופונים</DialogTitle>
            <DialogDescription>הדבק קודי קופונים, אחד בכל שורה</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="importCodes">קודי קופונים</Label>
              <textarea
                id="importCodes"
                value={importCodes}
                onChange={(e) => setImportCodes(e.target.value)}
                placeholder="1519746968&#10;6918292848&#10;4343612254"
                rows={10}
                className="w-full rounded-[10px] border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2 text-sm font-mono"
                dir="ltr"
              />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {importCodes.split('\n').filter((l) => l.trim()).length} קודים מוכנים לייבוא
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog(false)} disabled={importing}>
              ביטול
            </Button>
            <Button onClick={handleImportCoupons} disabled={importing}>
              {importing ? (
                <>
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  מייבא...
                </>
              ) : (
                'ייבוא'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
